import React, { useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { lensTokens, surfaceHairline } from '~/configs/unifiedAnalyticsTheme';
import MUIProvider from '~/contexts/MUIProvider';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand/brandTokens';

import { UnifiedAnalyticsThemeProvider } from '~/pages/AskRichardUnified/UnifiedAnalyticsThemeProvider';
import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';
import type { PageField } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import { download } from '../RmMonitor/liveModel';
import { csvFileName } from '../csvValues';

import {
  marketplaceFilterKeys,
  marketplaceViewParams,
  resetMarketplaceFilters,
  updateMarketplaceFilters,
} from './filterState';
import {
  type Choice,
  DEFAULT_PRODUCT,
  type LpcScope,
  type MarginMode,
  PAGE_ID,
  RECENT_DAYS,
  REQUESTED_CARDS,
  SPLITS,
  SPP_FACTS,
  SPP_WEEKS,
  type Series,
  type Split,
  type SppScope,
  VIEWS,
  type ViewId,
  WORST_LPC_VIEWS,
  WORST_SPP_IMPRESSIONS,
  chainName,
  distinct,
  integer,
  lpcCardRows,
  lpcChart,
  lpcUplift,
  lpcUpliftCsv,
  money0,
  money2,
  pct0,
  readLpcFacts,
  readLpcWeeks,
  readSppFacts,
  readSppWeeks,
  recentLpc,
  recentLpcCsv,
  recentSpp,
  recentSppCsv,
  sppChart,
  sppUplift,
  sppUpliftCsv,
  sydneyToday,
  thousands0,
  thousands1,
  weekWindows,
  worstLpc,
  worstLpcCsv,
  worstSpp,
  worstSppCsv,
} from './liveDataAdapter';
import { recentLaunchesSppScope, worstPerformersSppScope } from './worksheetScope';

const SCHEMA: Record<string, PageField> = {
  view: { type: 'enum', values: [...VIEWS] },
  split: { type: 'enum', values: [...SPLITS] },
  margin: { type: 'enum', values: ['Exclude', 'Include'] },
  product: { type: 'text', maxLength: 4000 },
  country: { type: 'text', maxLength: 4000 },
  channel: { type: 'text', maxLength: 200 },
  detail: { type: 'text', maxLength: 500 },
  chain: { type: 'text', maxLength: 4000 },
  vendor: { type: 'text', maxLength: 4000 },
  property: { type: 'text', maxLength: 4000 },
  action: { type: 'text', maxLength: 300 },
  dmin: { type: 'text', maxLength: 20 },
  dmax: { type: 'text', maxLength: 20 },
  sdmin: { type: 'text', maxLength: 20 },
  sdmax: { type: 'text', maxLength: 20 },
  wchannel: { type: 'text', maxLength: 200 },
  vmin: { type: 'text', maxLength: 20 },
  vmax: { type: 'text', maxLength: 20 },
  imin: { type: 'text', maxLength: 20 },
  imax: { type: 'text', maxLength: 20 },
};

function choice(params: URLSearchParams, key: string, fallback: Choice): Choice {
  const value = params.get(key);
  if (value === null) return fallback;
  if (value === '__ALL__' || value === 'all' || value === '') return 'all';
  return value.split('|').filter(Boolean);
}

function readView(value: string | null): ViewId {
  return VIEWS.includes(value as ViewId) ? (value as ViewId) : VIEWS[0];
}

function readSplit(value: string | null): Split {
  return SPLITS.includes(value as Split) ? (value as Split) : 'Vendors';
}

function readMargin(value: string | null): MarginMode {
  return value === 'Include' ? 'Include' : 'Exclude';
}

function readRange(params: URLSearchParams, minKey: string, maxKey: string, fallback: { min: number; max: number }) {
  const rawMin = params.get(minKey);
  const rawMax = params.get(maxKey);
  const min = rawMin === null || rawMin === '' ? NaN : Number(rawMin);
  const max = rawMax === null || rawMax === '' ? NaN : Number(rawMax);
  return {
    min: Number.isFinite(min) ? min : fallback.min,
    max: Number.isFinite(max) ? max : fallback.max,
  };
}

function cardRows(cards: Array<{ cardId?: string; rows?: unknown[] }>, id: string): Record<string, unknown>[] {
  const card = cards.find((item) => item.cardId === id);
  return (card?.rows ?? []) as Record<string, unknown>[];
}

function FilterSelect(props: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  return (
    <TextField
      select
      size="small"
      label={props.label}
      value={props.value}
      onChange={(event) => props.onChange(event.target.value)}
      sx={{ minWidth: 0, width: '100%' }}
    >
      {props.options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </TextField>
  );
}

function MemberSelect(props: { label: string; choice: Choice; options: string[]; onChange: (choice: Choice) => void }) {
  const value = props.choice === 'all' ? '__ALL__' : (props.choice[0] ?? '__ALL__');
  const options = [...new Set(['__ALL__', ...props.options.filter(Boolean), value])];
  return (
    <Autocomplete
      size="small"
      value={value}
      options={options}
      disableClearable
      getOptionLabel={(option) => (option === '__ALL__' ? 'All' : option)}
      onChange={(_, next) => props.onChange(next === '__ALL__' ? 'all' : [next])}
      renderInput={(input) => <TextField {...input} label={props.label} />}
      sx={{ minWidth: 0, width: '100%' }}
    />
  );
}

function FilterPanel({ children }: { children: React.ReactNode }) {
  const tokens = lensTokens(useTheme());
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: tokens.surface,
        border: surfaceHairline(tokens),
        borderRadius: 2,
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(auto-fit, minmax(180px, 1fr))',
        },
        gap: 1.5,
        // Range filters carry a caption above their Min/Max inputs; align every control on its input row.
        alignItems: 'end',
      }}
    >
      {children}
    </Paper>
  );
}

function Metric({ label, value, caption }: { label: string; value: string; caption: string }) {
  const tokens = lensTokens(useTheme());
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, bgcolor: tokens.surface, border: surfaceHairline(tokens), borderRadius: 2, minWidth: 0 }}
    >
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_STACK_SUISSE_INTL,
          fontSize: { xs: 26, md: 30 },
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          color: tokens.accent,
          lineHeight: 1.3,
          my: 0.5,
        }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    </Paper>
  );
}

function RangeFields(props: { label: string; min: number; max: number; onChange: (min: number, max: number) => void }) {
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {props.label}
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          type="number"
          label="Min"
          inputProps={{ 'aria-label': `${props.label} minimum` }}
          value={props.min}
          onChange={(event) => props.onChange(Number(event.target.value), props.max)}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <TextField
          size="small"
          type="number"
          label="Max"
          inputProps={{ 'aria-label': `${props.label} maximum` }}
          value={props.max}
          onChange={(event) => props.onChange(props.min, Number(event.target.value))}
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Stack>
    </Stack>
  );
}

function Trend({
  title,
  series,
  format,
}: {
  title: string;
  series: Series[];
  format: (value: number, seriesName: string) => string;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const colours = [
    tokens.accent,
    theme.palette.mode === 'dark' ? tokens.coralAccent : tokens.accent,
    theme.palette.text.secondary,
  ];
  const weeks = [...new Set(series.flatMap((item) => item.points.map((point) => point.week)))].sort();
  const values = series
    .flatMap((item) => item.points.map((point) => point.value))
    .filter((value): value is number => value !== null && Number.isFinite(value));
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;
  const span = max - min || 1;
  const width = 640;
  const height = 210;
  // Room for the widest axis label (about 7 px a character at 12 px), so $2,490,672 is not cut off.
  const ticks = [min, (min + max) / 2, max];
  const left = Math.max(68, Math.max(...ticks.map((value) => format(value, '').length)) * 7 + 14);
  const x = (index: number) => left + (weeks.length <= 1 ? 0 : index / (weeks.length - 1)) * (width - left - 12);
  const y = (value: number) => 12 + (1 - (value - min) / span) * (height - 48);
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 0,
        width: '100%',
        p: 2,
        bgcolor: tokens.surface,
        border: surfaceHairline(tokens),
        borderRadius: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {!values.length ? (
        <Typography color="text.secondary" sx={{ py: 6 }}>
          No trend data for this selection.
        </Typography>
      ) : (
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title} style={{ width: '100%', height: 210 }}>
          {ticks.map((value, index) => (
            <g key={index}>
              <line x1={left} x2={width - 12} y1={y(value)} y2={y(value)} stroke={tokens.grid} />
              <text x={left - 8} y={y(value) + 4} textAnchor="end" fill={theme.palette.text.secondary} fontSize={12}>
                {format(value, '')}
              </text>
            </g>
          ))}
          {series.map((item, index) => (
            <g key={item.name}>
              <polyline
                fill="none"
                stroke={colours[index % colours.length]}
                strokeDasharray={index === 1 ? '7 4' : undefined}
                strokeWidth={2.5}
                points={item.points
                  .filter((point) => point.value !== null)
                  .map((point) => `${x(weeks.indexOf(point.week))},${y(point.value as number)}`)
                  .join(' ')}
              />
              {item.points
                .filter((point) => point.value !== null)
                .map((point, pointIndex, points) => (
                  <Tooltip
                    key={point.week}
                    title={`${item.name} · ${point.week}: ${format(point.value as number, item.name)}`}
                  >
                    <circle
                      cx={x(weeks.indexOf(point.week))}
                      cy={y(point.value as number)}
                      r={5}
                      fill={colours[index % colours.length]}
                      fillOpacity={0}
                      tabIndex={pointIndex === points.length - 1 ? 0 : -1}
                      aria-label={`${item.name}, week ${point.week}, ${format(point.value as number, item.name)}`}
                      style={{ cursor: 'help' }}
                    />
                  </Tooltip>
                ))}
            </g>
          ))}
          <text x={left} y={height - 8} fill={theme.palette.text.secondary} fontSize={12}>
            {weeks[0]}
          </text>
          <text x={width - 12} y={height - 8} textAnchor="end" fill={theme.palette.text.secondary} fontSize={12}>
            {weeks[weeks.length - 1]}
          </text>
        </svg>
      )}
      <Stack direction="row" useFlexGap spacing={1.5} sx={{ flexWrap: 'wrap', mt: 1 }}>
        {series.map((item, index) => (
          <Typography
            key={item.name}
            variant="caption"
            sx={{ color: 'text.primary', display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            <Box
              component="span"
              sx={{
                width: 18,
                borderTop: `2px ${index === 1 ? 'dashed' : 'solid'} ${colours[index % colours.length]}`,
              }}
            />
            {item.name} {item.points.length ? format(item.points[item.points.length - 1].value ?? 0, item.name) : '—'}
          </Typography>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Weekly trend · latest full-week value · source: Marketplace Lens
      </Typography>
    </Paper>
  );
}

function ExportButton(props: { name: string; content: string; disabled: boolean }) {
  return (
    <Button
      size="small"
      variant="outlined"
      disabled={props.disabled}
      onClick={() => download(props.name, props.content)}
    >
      Export CSV
    </Button>
  );
}

function scrollTable(children: React.ReactNode, options: { alignLeft?: boolean } = {}) {
  return (
    <Box
      sx={(theme) => ({
        maxHeight: 480,
        overflow: 'auto',
        bgcolor: lensTokens(theme).surface,
        border: surfaceHairline(lensTokens(theme)),
        borderRadius: 2,
        '& .MuiTable-root': { minWidth: 640 },
        '& .MuiTableCell-root': { borderColor: 'divider', fontVariantNumeric: 'tabular-nums' },
        '& .MuiTableCell-stickyHeader': {
          bgcolor: lensTokens(theme).surfaceSunken,
          color: 'text.primary',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        },
        '& tbody .MuiTableCell-root:not(:first-of-type)': { textAlign: options.alignLeft ? 'left' : 'right' },
        '& tbody tr:has(> td:first-of-type[data-grand-total])': { bgcolor: lensTokens(theme).plateHover },
        '& .Mui-selected': { bgcolor: lensTokens(theme).plateHover },
      })}
    >
      {children}
    </Box>
  );
}

export function MarketplaceMonitoringContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const tokens = lensTokens(useTheme());
  const view = readView(new URLSearchParams(location.search).get('view'));
  const params = marketplaceViewParams(new URLSearchParams(location.search), view);
  const pageTitle = `Marketplace Monitoring · ${view}`;
  const filterKeys = marketplaceFilterKeys(view);
  const filterSchema = Object.fromEntries(Object.entries(SCHEMA).filter(([key]) => filterKeys.includes(key)));
  const split = readSplit(params.get('split'));
  const margin = readMargin(params.get('margin'));
  const product = choice(params, 'product', [DEFAULT_PRODUCT]);
  const country = choice(params, 'country', 'all');
  const channel = choice(params, 'channel', 'all');
  const detail = choice(params, 'detail', 'all');
  const chain = choice(params, 'chain', 'all');
  const vendor = choice(params, 'vendor', 'all');
  const property = choice(params, 'property', 'all');
  const action = params.get('action');
  const recentDays = readRange(params, 'dmin', 'dmax', RECENT_DAYS);
  const recentSppDays = readRange(params, 'sdmin', 'sdmax', RECENT_DAYS);
  const worstChannel = choice(params, 'wchannel', 'all');
  const worstViews = readRange(params, 'vmin', 'vmax', WORST_LPC_VIEWS);
  const worstImpressions = readRange(params, 'imin', 'imax', WORST_SPP_IMPRESSIONS);
  const today = sydneyToday();
  const windows = weekWindows(today);
  const query = useGetReportingLeLensData(
    nativeLensId(PAGE_ID),
    {},
    false,
    undefined,
    undefined,
    undefined,
    REQUESTED_CARDS,
  );
  const cards = query.data?.cards ?? [];
  const lpcRows = useMemo(() => lpcCardRows(cards), [cards]);
  const lpcFacts = useMemo(() => readLpcFacts(lpcRows?.facts ?? []), [lpcRows]);
  const lpcWeeks = useMemo(() => readLpcWeeks(lpcRows?.weeks ?? []), [lpcRows]);
  const sppFacts = useMemo(() => readSppFacts(cardRows(cards, SPP_FACTS)), [cards]);
  const sppWeeks = useMemo(() => readSppWeeks(cardRows(cards, SPP_WEEKS)), [cards]);
  const truncated = cards.some((card) => card.truncated);
  const ready =
    !query.isFetching &&
    !query.isError &&
    !query.isShowingPreviousData &&
    !truncated &&
    lpcRows !== null &&
    cards.some((card) => card.cardId === SPP_FACTS) &&
    cards.some((card) => card.cardId === SPP_WEEKS) &&
    cards.every((card) => card.status === 'ready' || card.status === 'empty');
  const lpcScope: LpcScope = { split, product, country, chain, vendor, action: view === VIEWS[0] ? action : null };
  const sppScope: SppScope = {
    split,
    margin,
    country,
    channel,
    detail,
    chain,
    property,
    action: view === VIEWS[1] ? action : null,
    eligibleOnly: true,
    brand: null,
  };
  const uplift = useMemo(() => lpcUplift(lpcFacts, lpcScope), [lpcFacts, split, product, country, chain, vendor]);
  const sppTable = useMemo(
    () => sppUplift(sppFacts, sppScope),
    [sppFacts, split, margin, country, channel, detail, chain, property],
  );
  const launchesLpc = useMemo(
    () => recentLpc(lpcFacts, { ...lpcScope, action: null }, today, recentDays),
    [lpcFacts, country, chain, vendor, today, recentDays.min, recentDays.max],
  );
  const launchesSpp = useMemo(
    () => recentSpp(sppFacts, recentLaunchesSppScope(sppScope), today, recentSppDays),
    [sppFacts, margin, country, chain, property, today, recentSppDays.min, recentSppDays.max],
  );
  const worstLpcRows = useMemo(
    () => worstLpc(lpcFacts, { ...lpcScope, action: null }, worstViews),
    [lpcFacts, country, worstViews.min, worstViews.max],
  );
  const worstSppRows = useMemo(
    () => worstSpp(sppFacts, worstPerformersSppScope(sppScope, worstChannel), worstImpressions),
    [sppFacts, country, worstChannel, worstImpressions.min, worstImpressions.max],
  );
  const mpvSeries = useMemo(() => lpcChart(lpcWeeks, lpcFacts, lpcScope, 'mpv'), [lpcWeeks, lpcFacts, lpcScope]);
  const viewSeries = useMemo(() => lpcChart(lpcWeeks, lpcFacts, lpcScope, 'views'), [lpcWeeks, lpcFacts, lpcScope]);
  const gmSeries = useMemo(() => lpcChart(lpcWeeks, lpcFacts, lpcScope, 'gm'), [lpcWeeks, lpcFacts, lpcScope]);
  const mpiSeries = useMemo(
    () => sppChart(sppWeeks, sppFacts, lpcWeeks, sppScope, 'mpi'),
    [sppWeeks, sppFacts, lpcWeeks, sppScope],
  );
  const impressionSeries = useMemo(
    () => sppChart(sppWeeks, sppFacts, lpcWeeks, sppScope, 'impressions'),
    [sppWeeks, sppFacts, lpcWeeks, sppScope],
  );
  const sppViewSeries = useMemo(
    () => sppChart(sppWeeks, sppFacts, lpcWeeks, sppScope, 'views'),
    [sppWeeks, sppFacts, lpcWeeks, sppScope],
  );
  const sppGmSeries = useMemo(
    () => sppChart(sppWeeks, sppFacts, lpcWeeks, sppScope, 'gm'),
    [sppWeeks, sppFacts, lpcWeeks, sppScope],
  );
  const update = (changes: Record<string, string | null>) => {
    const nextView = 'view' in changes ? readView(changes.view) : view;
    const next = updateMarketplaceFilters(params, changes, nextView);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  };
  const reset = () =>
    navigate({ pathname: location.pathname, search: resetMarketplaceFilters(params).toString() }, { replace: true });
  const writeChoice = (key: string, value: Choice, fallback: Choice) => {
    if (value === 'all') update({ [key]: fallback === 'all' ? null : '__ALL__' });
    else update({ [key]: value.join('|') });
  };
  const toggleAction = (splitValue: string) => update({ action: action === splitValue ? null : splitValue });
  usePageBridge({
    pageId: PAGE_ID,
    view: {
      title: pageTitle,
      ready,
      filterSchema,
      filters: Object.fromEntries(
        Object.entries({
          view,
          split,
          margin,
          product: product === 'all' ? 'all' : product.join('|'),
          country: country === 'all' ? 'all' : country.join('|'),
          channel: channel === 'all' ? 'all' : channel.join('|'),
          detail: detail === 'all' ? 'all' : detail.join('|'),
          chain: chain === 'all' ? 'all' : chain.join('|'),
          vendor: vendor === 'all' ? 'all' : vendor.join('|'),
          property: property === 'all' ? 'all' : property.join('|'),
          action: action ?? '',
          dmin: String(recentDays.min),
          dmax: String(recentDays.max),
          sdmin: String(recentSppDays.min),
          sdmax: String(recentSppDays.max),
          wchannel: worstChannel === 'all' ? 'all' : worstChannel.join('|'),
          vmin: String(worstViews.min),
          vmax: String(worstViews.max),
          imin: String(worstImpressions.min),
          imax: String(worstImpressions.max),
        }).filter(([key]) => filterKeys.includes(key)),
      ),
      dataMode: 'live BigQuery through Lens',
      summary: {
        worksheet: view,
        lastWeek: windows.lastWeek,
        ...(view === VIEWS[0] ? { lpcGmVs: uplift.grand.gmVs, lpcGmLw: uplift.grand.gmLw } : {}),
        rows:
          view === VIEWS[0]
            ? uplift.rows.length
            : view === VIEWS[1]
              ? sppTable.rows.length
              : view === VIEWS[2]
                ? launchesLpc.length + launchesSpp.length
                : worstLpcRows.length + worstSppRows.length,
        ...(view === VIEWS[2] ? { lpcRows: launchesLpc.length, sppRows: launchesSpp.length } : {}),
        ...(view === VIEWS[3] ? { lpcRows: worstLpcRows.length, sppRows: worstSppRows.length } : {}),
        action,
        complete: ready,
      },
      definitions: {
        windows:
          'Last week is the Monday week before this Monday in Australia/Sydney. GM and views versus the prior four weeks use the four Monday weeks before last week, divided by 4. MpV and MpPOI versus the prior four weeks use the four Monday weeks ending last week, as a ratio of sums.',
        productName:
          'Product Name2 maps an active SPP flag on Lux Premium Collection, Lux Partner Property or Limited Time Lux Partner to Single Property Page (SPP), and the rest of Lux Premium Collection to Lux Premium Collection (Non-SPP).',
        margin:
          'Margin Max Exclude uses gm minus gm_max for MpPOI and the SPP gross-margin lines. Margin vs P4W on the table is GM last week minus GM prior-four-weeks and stays on gm.',
        countryRate:
          'MpV vs Country compares an offer’s last-week MpV with the offer country’s last-week MpV for product_name Lux Premium Collection. The country rate is attached once per country.',
        views:
          'views, views_sincelaunch, impressions and view_count are already summed on each source row. Worksheets sum them again.',
        action:
          'Selecting a row filters the charts on that view by the current split. Selecting the same row again clears the filter. The source table stays in place.',
      },
    },
    edit: (changes) => {
      if (!ready) throw new Error('Page data is still loading.');
      validatePageFilters(changes, filterSchema);
      const next: Record<string, string | null> = {};
      Object.entries(changes).forEach(([key, value]) => {
        next[key] = value === null ? null : String(value);
      });
      update(next);
    },
  });
  const products = distinct(lpcFacts.map((fact) => fact.product));
  const countries = distinct([...lpcFacts.map((fact) => fact.country), ...sppFacts.map((fact) => fact.country)]);
  const chains = distinct([
    ...lpcFacts.map((fact) => chainName(fact.parent)),
    ...sppFacts.map((fact) => chainName(fact.parent)),
  ]);
  const vendors = distinct(lpcFacts.map((fact) => fact.vendor));
  const properties = distinct(sppFacts.map((fact) => fact.property));
  const channels = distinct(sppFacts.map((fact) => fact.channel));
  const details = distinct(
    sppFacts
      .filter((fact) => channel === 'all' || (fact.channel !== null && channel.includes(fact.channel)))
      .map((fact) => fact.detail),
  );
  const activeFilters = [...params.entries()].filter(
    ([key, value]) => key !== 'view' && key !== 'library' && value !== '__ALL__',
  );
  const filterLabels: Record<string, string> = {
    split: 'Split by',
    margin: 'Margin Max',
    product: 'Product',
    country: 'Offer country',
    chain: 'Chain',
    vendor: 'Vendor',
    property: 'Vendor (SPP)',
    channel: 'Channel',
    detail: 'Channel detail',
    action: 'Chart selection',
    dmin: 'LPC launch days min',
    dmax: 'LPC launch days max',
    sdmin: 'SPP launch days min',
    sdmax: 'SPP launch days max',
    wchannel: 'SPP channel',
    vmin: 'LPC views min',
    vmax: 'LPC views max',
    imin: 'SPP impressions min',
    imax: 'SPP impressions max',
  };
  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        display: 'grid',
        gap: 2.5,
        minWidth: 0,
        bgcolor: tokens.canvas,
        color: 'text.primary',
        '& > *': { minWidth: 0 },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ justifyContent: 'space-between', gap: 2, alignItems: { md: 'center' } }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Commercial performance
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontFamily: FONT_STACK_SUISSE_INTL,
              fontSize: 23,
              fontWeight: 600,
              color: tokens.accent,
              lineHeight: 1.25,
              mt: 0.5,
            }}
          >
            {pageTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Compare channels, understand movement and identify underperformance.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, maxWidth: '100%' }}
        >
          <Paper
            variant="outlined"
            sx={{ p: 2, bgcolor: tokens.surface, border: surfaceHairline(tokens), borderRadius: 2 }}
          >
            <Typography variant="caption" color="text.secondary">
              Latest full week · Australia/Sydney
            </Typography>
            <Typography variant="subtitle1" sx={{ color: tokens.accent }}>
              {windows.lastWeek}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Compared with the prior four weeks · current week excluded
            </Typography>
          </Paper>
          <PageChatButton />
        </Stack>
      </Stack>
      <Tabs
        aria-label="Marketplace worksheets"
        value={view}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        onChange={(_, value: ViewId) => update({ view: value })}
        sx={{
          borderBottom: surfaceHairline(tokens),
          '& .MuiTab-root': { textTransform: 'none', minHeight: 48, px: 2 },
        }}
      >
        {VIEWS.map((item) => (
          <Tab key={item} value={item} label={item} />
        ))}
      </Tabs>
      {ready ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          sx={{ gap: 1.5, justifyContent: 'space-between', alignItems: { sm: 'center' } }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">
              {activeFilters.length ? 'Active filters' : 'Default report scope'}
            </Typography>
            <Stack direction="row" useFlexGap spacing={0.75} sx={{ flexWrap: 'wrap', mt: 0.75 }}>
              {activeFilters.length ? (
                activeFilters.map(([key, value]) => (
                  <Chip
                    key={key}
                    size="small"
                    label={`${filterLabels[key] ?? key}: ${value.replace(/\|/g, ', ')}`}
                    onDelete={() => update({ [key]: null })}
                    sx={{
                      maxWidth: '100%',
                      height: 'auto',
                      minHeight: 28,
                      '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5, overflowWrap: 'anywhere' },
                    }}
                  />
                ))
              ) : (
                <Typography variant="caption" color="text.secondary">
                  All available data within this worksheet’s defaults.
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              Filters apply to this worksheet. Shared filters carry across; unavailable filters clear when you switch.
            </Typography>
          </Box>
          <Button variant="text" onClick={reset} disabled={!activeFilters.length} sx={{ flexShrink: 0 }}>
            Reset filters
          </Button>
        </Stack>
      ) : null}
      {!ready && !query.isError ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}
      {query.isError || truncated ? (
        <Alert severity="warning" action={<Button onClick={() => query.refetch()}>Retry</Button>}>
          {truncated
            ? 'Marketplace results are incomplete. Retry before using these numbers.'
            : 'Marketplace data is unavailable right now. Try loading the report again.'}
        </Alert>
      ) : null}
      {ready && view === VIEWS[0] ? (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <FilterPanel>
            <FilterSelect
              label="Split by"
              value={split}
              options={[...SPLITS]}
              onChange={(value) => update({ split: value, action: null })}
            />
            <MemberSelect
              label="Product Name"
              choice={product}
              options={products}
              onChange={(value) => writeChoice('product', value, [DEFAULT_PRODUCT])}
            />
            <MemberSelect
              label="Offer Country"
              choice={country}
              options={countries}
              onChange={(value) => writeChoice('country', value, 'all')}
            />
            <MemberSelect
              label="Chain"
              choice={chain}
              options={chains}
              onChange={(value) => writeChoice('chain', value, 'all')}
            />
            <MemberSelect
              label="Vendor"
              choice={vendor}
              options={vendors}
              onChange={(value) => writeChoice('vendor', value, 'all')}
            />
          </FilterPanel>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            <Metric
              label="Margin per view"
              value={pct0(uplift.grand.mpvVs)}
              caption="Change versus the prior four weeks"
            />
            <Metric
              label="Offer views"
              value={pct0(uplift.grand.viewsVs)}
              caption="Change versus the prior four weeks"
            />
            <Metric
              label="Gross margin"
              value={money0(uplift.grand.gmVs)}
              caption="Movement versus the prior four weeks"
            />
          </Box>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle2">
              Performance by {split.toLowerCase()} · {integer(uplift.rows.length)} groups
            </Typography>
            <ExportButton
              name={csvFileName('uplift-vs-p4w_last-week', windows.lastWeek)}
              content={lpcUpliftCsv(uplift.rows, uplift.grand)}
              disabled={!ready}
            />
          </Stack>
          {uplift.rows.length === 0 ? (
            <Alert severity="info" action={<Button onClick={reset}>Reset filters</Button>}>
              No matching LPC results. Try a broader selection.
            </Alert>
          ) : null}
          {scrollTable(
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Data Split', 'MpV vs P4W', 'Views vs P4W', 'GM vs P4W'].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell data-grand-total>Grand Total</TableCell>
                  <TableCell>{pct0(uplift.grand.mpvVs)}</TableCell>
                  <TableCell>{pct0(uplift.grand.viewsVs)}</TableCell>
                  <TableCell>{money0(uplift.grand.gmVs)}</TableCell>
                </TableRow>
                {uplift.rows.map((row) => (
                  <TableRow
                    key={row.split}
                    hover
                    selected={action === row.split}
                    tabIndex={0}
                    aria-selected={action === row.split}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleAction(row.split);
                      }
                    }}
                    onClick={() => toggleAction(row.split)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{row.split}</TableCell>
                    <TableCell>{pct0(row.mpvVs)}</TableCell>
                    <TableCell>{pct0(row.viewsVs)}</TableCell>
                    <TableCell>{money0(row.gmVs)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>,
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(3, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <Trend
              title={action ? `MpV · ${action}` : 'MpV · LPC (Non-SPP) vs LTLE'}
              series={mpvSeries}
              format={money2}
            />
            <Trend
              title={action ? `Views · ${action}` : 'Views · LPC (Non-SPP) vs LTLE'}
              series={viewSeries}
              format={integer}
            />
            <Trend
              title={action ? `Gross margin · ${action}` : 'Gross margin · LPC (Non-SPP) vs LTLE'}
              series={gmSeries}
              format={money0}
            />
          </Box>
        </Box>
      ) : null}
      {ready && view === VIEWS[1] ? (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <FilterPanel>
            <FilterSelect
              label="Split by"
              value={split}
              options={[...SPLITS]}
              onChange={(value) => update({ split: value, action: null })}
            />
            <FilterSelect
              label="Margin Max"
              value={margin}
              options={['Exclude', 'Include']}
              onChange={(value) => update({ margin: value })}
            />
            <MemberSelect
              label="Channel"
              choice={channel}
              options={channels}
              onChange={(value) => writeChoice('channel', value, 'all')}
            />
            <MemberSelect
              label="Channel Detail"
              choice={detail}
              options={details}
              onChange={(value) => writeChoice('detail', value, 'all')}
            />
            <MemberSelect
              label="Offer Country"
              choice={country}
              options={countries}
              onChange={(value) => writeChoice('country', value, 'all')}
            />
            <MemberSelect
              label="Chain"
              choice={chain}
              options={chains}
              onChange={(value) => writeChoice('chain', value, 'all')}
            />
            <MemberSelect
              label="Vendor (SPP)"
              choice={property}
              options={properties}
              onChange={(value) => writeChoice('property', value, 'all')}
            />
          </FilterPanel>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            <Metric
              label="Gross margin last week"
              value={money0(Object.values(sppTable.grand).reduce((sum, item) => sum + item.gmLw, 0))}
              caption="Across channels in the current selection"
            />
            <Metric
              label="Gross margin movement"
              value={money0(Object.values(sppTable.grand).reduce((sum, item) => sum + item.marginVs, 0))}
              caption="Versus the prior four weeks"
            />
            <Metric
              label="Comparison groups"
              value={integer(sppTable.rows.length)}
              caption={`Grouped by ${split.toLowerCase()} · eligible properties only`}
            />
          </Box>
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2">SPP performance · {integer(sppTable.rows.length)} groups</Typography>
              <Typography variant="caption" color="text.secondary">
                Eligible properties · select a row to focus the charts
              </Typography>
            </Box>
            <ExportButton
              name={csvFileName('uplift-vs-p4w-spp_last-week', windows.lastWeek)}
              content={sppUpliftCsv(sppTable.rows, sppTable.grand)}
              disabled={!ready}
            />
          </Stack>
          {sppTable.rows.length === 0 ? (
            <Alert severity="info" action={<Button onClick={reset}>Reset filters</Button>}>
              No matching SPP results. Try a broader channel or detail selection.
            </Alert>
          ) : null}
          {scrollTable(
            <Table
              size="small"
              stickyHeader
              sx={{
                '& thead tr:first-of-type th': { height: 40, boxSizing: 'border-box', top: 0, zIndex: 3 },
                '& thead tr:nth-of-type(2) th': { top: 40, zIndex: 2 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2}>Data Split</TableCell>
                  {sppTable.channels.map((item) => (
                    <TableCell key={item} colSpan={4} align="center">
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {sppTable.channels.flatMap((item) =>
                    ['MpPOI vs P4W', 'Impres. vs P4W', 'Views vs P4W', 'Margin vs P4W'].map((label) => (
                      <TableCell key={`${item}-${label}`}>{label}</TableCell>
                    )),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell data-grand-total>Grand Total</TableCell>
                  {sppTable.channels.flatMap((item) => {
                    const cell = sppTable.grand[item];
                    return [
                      <TableCell key={`${item}-m`}>{pct0(cell?.mppoiVs ?? null)}</TableCell>,
                      <TableCell key={`${item}-i`}>{pct0(cell?.impVs ?? null)}</TableCell>,
                      <TableCell key={`${item}-v`}>{pct0(cell?.viewsVs ?? null)}</TableCell>,
                      <TableCell key={`${item}-g`}>{money0(cell?.marginVs ?? null)}</TableCell>,
                    ];
                  })}
                </TableRow>
                {sppTable.rows.map((row) => (
                  <TableRow
                    key={row.split}
                    hover
                    selected={action === row.split}
                    tabIndex={0}
                    aria-selected={action === row.split}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleAction(row.split);
                      }
                    }}
                    onClick={() => toggleAction(row.split)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{row.split}</TableCell>
                    {sppTable.channels.flatMap((item) => {
                      const cell = row.channels[item];
                      return [
                        <TableCell key={`${row.split}-${item}-m`}>{pct0(cell?.mppoiVs ?? null)}</TableCell>,
                        <TableCell key={`${row.split}-${item}-i`}>{pct0(cell?.impVs ?? null)}</TableCell>,
                        <TableCell key={`${row.split}-${item}-v`}>{pct0(cell?.viewsVs ?? null)}</TableCell>,
                        <TableCell key={`${row.split}-${item}-g`}>{money0(cell?.marginVs ?? null)}</TableCell>,
                      ];
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>,
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <Trend title={action ? `MpPOI · ${action}` : 'MpPOI · LPC vs LPP'} series={mpiSeries} format={money2} />
            <Trend
              title={action ? `Impressions · ${action}` : 'Impressions · LPC vs LPP'}
              series={impressionSeries}
              format={integer}
            />
            <Trend
              title={action ? `Views · ${action}` : 'Views · LPC vs LTLE'}
              series={sppViewSeries}
              format={integer}
            />
            <Trend
              title={action ? `Gross margin · ${action}` : 'Gross margin · SPP vs LTLE'}
              series={sppGmSeries}
              format={(value, name) => (name === 'LTLE' ? thousands0(value) : thousands1(value))}
            />
          </Box>
        </Box>
      ) : null}
      {ready && view === VIEWS[2] ? (
        <Box sx={{ display: 'grid', gap: 3 }}>
          <FilterPanel>
            <MemberSelect
              label="Offer Country"
              choice={country}
              options={countries}
              onChange={(value) => writeChoice('country', value, 'all')}
            />
            <MemberSelect
              label="Chain"
              choice={chain}
              options={chains}
              onChange={(value) => writeChoice('chain', value, 'all')}
            />
            <MemberSelect
              label="Vendor Name (LPC Non-SPP)"
              choice={vendor}
              options={vendors}
              onChange={(value) => writeChoice('vendor', value, 'all')}
            />
            <MemberSelect
              label="Vendor Name (SPP)"
              choice={property}
              options={properties}
              onChange={(value) => writeChoice('property', value, 'all')}
            />
            <RangeFields
              label="LPC days since launch"
              min={recentDays.min}
              max={recentDays.max}
              onChange={(min, max) => update({ dmin: String(min), dmax: String(max) })}
            />
            <RangeFields
              label="SPP days since launch"
              min={recentSppDays.min}
              max={recentSppDays.max}
              onChange={(min, max) => update({ sdmin: String(min), sdmax: String(max) })}
            />
            <FilterSelect
              label="Margin Max"
              value={margin}
              options={['Exclude', 'Include']}
              onChange={(value) => update({ margin: value })}
            />
          </FilterPanel>
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Typography variant="h6">LPC (Non-SPP)</Typography>
            <ExportButton
              name={csvFileName('recent-launches-lpc_last-week', windows.lastWeek)}
              content={recentLpcCsv(launchesLpc)}
              disabled={!ready}
            />
          </Stack>
          {scrollTable(
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    'Days',
                    'Planned Run Date',
                    'Chain',
                    'Vendor',
                    'Offer Country',
                    'Latest Id',
                    'MpV LW',
                    'GM LW',
                    'Views LW',
                    'MpV Since Launch',
                    'GM Since Launch',
                    'Views Since Launch',
                  ].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {launchesLpc.map((row) => (
                  <TableRow key={`${row.latestId}-${row.country}`}>
                    <TableCell>{integer(row.days)}</TableCell>
                    <TableCell>{row.planned}</TableCell>
                    <TableCell>{row.chain}</TableCell>
                    <TableCell>{row.vendor}</TableCell>
                    <TableCell>{row.country}</TableCell>
                    <TableCell>{row.latestId}</TableCell>
                    <TableCell>{money2(row.mpvLw)}</TableCell>
                    <TableCell>{money0(row.gmLw)}</TableCell>
                    <TableCell>{integer(row.viewsLw)}</TableCell>
                    <TableCell>{money2(row.mpvSince)}</TableCell>
                    <TableCell>{money0(row.gmSince)}</TableCell>
                    <TableCell>{integer(row.viewsSince)}</TableCell>
                  </TableRow>
                ))}
                {!launchesLpc.length ? (
                  <TableRow>
                    <TableCell colSpan={12} sx={{ py: 4, textAlign: 'center !important', color: 'text.secondary' }}>
                      No matching results. Broaden the filters or use Reset filters.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>,
            { alignLeft: true },
          )}
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Typography variant="h6">SPP</Typography>
            <ExportButton
              name={csvFileName('recent-launches-spp_last-week', windows.lastWeek)}
              content={recentSppCsv(launchesSpp)}
              disabled={!ready}
            />
          </Stack>
          {scrollTable(
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    'Days',
                    'Planned Run Date',
                    'Chain',
                    'Property',
                    'Offer Country',
                    'Latest Id',
                    'MpPOI LW',
                    'GM LW',
                    'Impressions LW',
                    'Impressions Since Launch',
                  ].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {launchesSpp.map((row) => (
                  <TableRow key={`${row.latestId}-${row.country}-${row.property}`}>
                    <TableCell>{integer(row.days)}</TableCell>
                    <TableCell>{row.planned}</TableCell>
                    <TableCell>{row.chain}</TableCell>
                    <TableCell>{row.property}</TableCell>
                    <TableCell>{row.country}</TableCell>
                    <TableCell>{row.latestId}</TableCell>
                    <TableCell>{money2(row.mppoiLw)}</TableCell>
                    <TableCell>{money0(row.gmLw)}</TableCell>
                    <TableCell>{integer(row.impressionsLw)}</TableCell>
                    <TableCell>{integer(row.impressionsSince)}</TableCell>
                  </TableRow>
                ))}
                {!launchesSpp.length ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 4, textAlign: 'center !important', color: 'text.secondary' }}>
                      No matching results. Broaden the filters or use Reset filters.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>,
            { alignLeft: true },
          )}
        </Box>
      ) : null}
      {ready && view === VIEWS[3] ? (
        <Box sx={{ display: 'grid', gap: 3 }}>
          <FilterPanel>
            <RangeFields
              label="LPC views last week"
              min={worstViews.min}
              max={worstViews.max}
              onChange={(min, max) => update({ vmin: String(min), vmax: String(max) })}
            />
            <MemberSelect
              label="SPP Channel"
              choice={worstChannel}
              options={channels}
              onChange={(value) => writeChoice('wchannel', value, 'all')}
            />
            <RangeFields
              label="SPP impressions last week"
              min={worstImpressions.min}
              max={worstImpressions.max}
              onChange={(min, max) => update({ imin: String(min), imax: String(max) })}
            />
            <MemberSelect
              label="Offer Country"
              choice={country}
              options={countries}
              onChange={(value) => writeChoice('country', value, 'all')}
            />
          </FilterPanel>
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Typography variant="h6">LPC (Non-SPP)</Typography>
            <ExportButton
              name={csvFileName('worst-lpc_last-week', windows.lastWeek)}
              content={worstLpcCsv(worstLpcRows)}
              disabled={!ready}
            />
          </Stack>
          {scrollTable(
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    'Vendor',
                    'Offer Country',
                    'Latest Id',
                    'Planned Run Date',
                    'MpV LW',
                    'MpV vs P4W',
                    'MpV vs Country',
                    'Views LW',
                    'Views vs P4W',
                    'GM LW',
                    'GM vs P4W',
                  ].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {worstLpcRows.map((row) => (
                  <TableRow key={`${row.latestId}-${row.country}`}>
                    <TableCell>{row.vendor}</TableCell>
                    <TableCell>{row.country}</TableCell>
                    <TableCell>{row.latestId}</TableCell>
                    <TableCell>{row.planned}</TableCell>
                    <TableCell>{money2(row.mpvLw)}</TableCell>
                    <TableCell>{pct0(row.mpvVs)}</TableCell>
                    <TableCell>{pct0(row.mpvDiff)}</TableCell>
                    <TableCell>{integer(row.viewsLw)}</TableCell>
                    <TableCell>{pct0(row.viewsVs)}</TableCell>
                    <TableCell>{money0(row.gmLw)}</TableCell>
                    <TableCell>{money0(row.gmVs)}</TableCell>
                  </TableRow>
                ))}
                {!worstLpcRows.length ? (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ py: 4, textAlign: 'center !important', color: 'text.secondary' }}>
                      No matching results. Broaden the filters or use Reset filters.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>,
          )}
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Typography variant="h6">SPP</Typography>
            <ExportButton
              name={csvFileName('worst-spp_last-week', windows.lastWeek)}
              content={worstSppCsv(worstSppRows)}
              disabled={!ready}
            />
          </Stack>
          {scrollTable(
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    'Property',
                    'Offer Country',
                    'Latest Id',
                    'Planned Run Date',
                    'MpPOI LW',
                    'MpPOI vs P4W',
                    'Impressions LW',
                    'Impres. vs P4W',
                    'GM LW',
                    'GM vs P4W',
                  ].map((label) => (
                    <TableCell key={label}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {worstSppRows.map((row) => (
                  <TableRow key={`${row.latestId}-${row.country}-${row.property}`}>
                    <TableCell>{row.property}</TableCell>
                    <TableCell>{row.country}</TableCell>
                    <TableCell>{row.latestId}</TableCell>
                    <TableCell>{row.planned}</TableCell>
                    <TableCell>{money2(row.mppoiLw)}</TableCell>
                    <TableCell>{pct0(row.mppoiVs)}</TableCell>
                    <TableCell>{integer(row.impressionsLw)}</TableCell>
                    <TableCell>{pct0(row.impVs)}</TableCell>
                    <TableCell>{money0(row.gmLw)}</TableCell>
                    <TableCell>{money0(row.gmVs)}</TableCell>
                  </TableRow>
                ))}
                {!worstSppRows.length ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 4, textAlign: 'center !important', color: 'text.secondary' }}>
                      No matching results. Broaden the filters or use Reset filters.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>,
          )}
        </Box>
      ) : null}
    </Box>
  );
}

export default function MarketplaceMonitoringPage() {
  return (
    <MUIProvider>
      <UnifiedAnalyticsThemeProvider>
        <PageChatLayout>
          <MarketplaceMonitoringContent />
        </PageChatLayout>
      </UnifiedAnalyticsThemeProvider>
    </MUIProvider>
  );
}
